import type { ProfessionCardObj } from '~/types/profession'

/** Option domaine / secteur (valeur = libellé, comme sur l'ancien site). */
export interface MetiersSearchFilterOption {
  value: string
  label: string
}

/**
 * Lignes mock pour la grille : carte métier + filtres domaine / secteur.
 */
export interface MetiersSearchProfessionRow extends ProfessionCardObj {
  domainValue: string
  sectorValue: string
}

const DOMAIN_LABELS: readonly string[] = [
  'Agriculture, Alimentation, Environnement, Energie',
  'Architecture intérieur design',
  'Architecture, Urbanisme',
  'Art, Culture, Design, Mode',
  'Artisanat',
  'BTP, Bâtiment, Construction',
  'Cinéma, Audiovisuel, Sons',
  'Commerce, Management, RH',
  'Droit, Sciences Politiques',
  'Enseignement, Fonction publique',
  'Finance, Banque, Comptabilité',
  'Fonction publique, Armée, Sécurité, Défense',
  'Hôtellerie, Restauration, Tourisme',
  'Immobilier',
  'Industrie, Chimie',
  'Ingénierie, Mécanique',
  'Innovation, Entrepreneuriat',
  'Langues, Sciences Humaines, Lettres',
  'Marketing, Communication, Edition, Journalisme',
  'Métiers de bouche',
  'Numérique, Digital, Informatique, Programmation',
  'Orientation et renforcement',
  'Santé, Paramédical, Social, Aide à la personne',
  'Sport, Bien-être, Soins',
  'Transport, Logistique, Distribution',
] as const

const SECTOR_LABELS: readonly string[] = [
  'Administratif',
  'Administration',
  'Aéronautique et transport aérien',
  'Agriculture',
  'Agroalimentaire',
  'Animation socioculturelle',
  'Architecte - BTP - Urbanisme',
  'Architecture - BTP - Urbanisme',
  'Armée de Terre',
  'Art',
  'Artisanat',
  'Assurance',
  'Audiovisuel - Cinéma',
  'Audit - Comptabilité - Finance',
  'Automobile',
  'Banque - Finance',
  'BTP',
  'Chimie - Biologie',
  'Commerce - Vente - Distribution',
  'Communication',
  'Comptabilité - Gestion',
  'Conseil en stratégie',
  'Consultant(e)progiciel',
  'Consulting - Conseil',
  'Création',
  'Culture',
  'Cybersécurité',
  'Dirigeant/Tous',
  'Droit - Justice',
  'Economie',
  'Économie',
  'Édition et métiers du livre',
  'Energie',
  'Énergie',
  'Enseignement',
  'Environnement - Développement durable',
  'Esthétique - Beauté - Coiffure',
  'Études et conseils économiques/financiers',
  'Événementiel',
  'Finance',
  'Fonction publique - Management public',
  'Fonction publique / Management public',
  'Formation professionnelle',
  'Hôtellerie-Restauration',
  'Humanitaire',
  'Immobilier',
  'Industrie',
  'Informatique - Electronique - Numérique',
  'Informatique - Électronique - Numérique',
  'Internet - Web',
  'Jeu vidéo - Esport - Gaming',
  'Journalisme',
  'Logistique',
  'Luxe',
  'Métiers animaliers',
  'Métiers de bouche',
  'Métiers de la mer',
  'Métiers du vin',
  'Mode - Textile',
  'Multimédia',
  'Musique',
  'Paramédical',
  'Photographie - Arts visuels',
  'Propreté et services aux entreprises',
  'Psychologie',
  'Publicité - Marketing',
  'Ressources Humaines',
  'Santé',
  'Secrétaire-Assistant(e)',
  'Sécurité - Armée - Défense',
  'Services funéraires',
  'Social',
  'Sport',
  'Tourisme',
  'Transport - Logistique',
  'Travailler avec les enfants',
] as const

/** Liste « Par domaines (Tous) » + domaines (contenu ancien site). */
/** Première entrée : `value` vide ; le libellé « Tous » est injecté côté page (i18n). */
export const METIERS_DOMAIN_OPTIONS: MetiersSearchFilterOption[] = [
  { value: '', label: '' },
  ...DOMAIN_LABELS.map((label: string): MetiersSearchFilterOption => ({ value: label, label })),
]

/** Première entrée : `value` vide ; le libellé « Tous » est injecté côté page (i18n). */
export const METIERS_SECTOR_OPTIONS: MetiersSearchFilterOption[] = [
  { value: '', label: '' },
  ...SECTOR_LABELS.map((label: string): MetiersSearchFilterOption => ({ value: label, label })),
]

/**
 * Métier mock pour la page recherche (grille + filtres).
 * @returns {MetiersSearchProfessionRow[]} Liste déterministe pour démo filtres.
 */
export function getMockMetiersSearchProfessions(): MetiersSearchProfessionRow[] {
  const img: string = '/images/emptyCoverPicture.png'
  const rows: Omit<MetiersSearchProfessionRow, 'id'>[] = [
    {
      Name: 'Cloutier artisanal',
      Description: 'Fabrication et réparation de chaussures et articles en cuir.',
      Domain: 'Artisanat',
      Sector: 'Artisanat',
      Salary: '1 600 – 2 200 € / mois',
      BackgroundImage: img,
      Link: '/metiers/artisanat/artisanat/cloutier-artisanal',
      domainValue: 'Artisanat',
      sectorValue: 'Artisanat',
    },
    {
      Name: 'Développeur web',
      Description: "Conception d'applications et sites web.",
      Domain: 'Numérique, Digital, Informatique, Programmation',
      Sector: 'Informatique - Électronique - Numérique',
      Salary: '2 200 – 3 500 € / mois',
      BackgroundImage: img,
      Link: '/metiers/numerique/developpeur-web',
      domainValue: 'Numérique, Digital, Informatique, Programmation',
      sectorValue: 'Informatique - Électronique - Numérique',
    },
    {
      Name: 'Infirmier(ère)',
      Description: 'Soins directs aux patients en milieu hospitalier ou libéral.',
      Domain: 'Santé, Paramédical, Social, Aide à la personne',
      Sector: 'Santé',
      Salary: '1 900 – 2 800 € / mois',
      BackgroundImage: img,
      Link: '/metiers/sante/infirmier',
      domainValue: 'Santé, Paramédical, Social, Aide à la personne',
      sectorValue: 'Santé',
    },
    {
      Name: 'Architecte',
      Description: 'Conception de bâtiments et espaces urbains.',
      Domain: 'Architecture, Urbanisme',
      Sector: 'Architecture - BTP - Urbanisme',
      Salary: '2 500 – 4 000 € / mois',
      BackgroundImage: img,
      Link: '/metiers/architecture/architecte',
      domainValue: 'Architecture, Urbanisme',
      sectorValue: 'Architecture - BTP - Urbanisme',
    },
    {
      Name: 'Commercial B2B',
      Description: 'Vente et relation client entreprises.',
      Domain: 'Commerce, Management, RH',
      Sector: 'Commerce - Vente - Distribution',
      Salary: '2 000 – 3 200 € / mois',
      BackgroundImage: img,
      Link: '/metiers/commerce/commercial-b2b',
      domainValue: 'Commerce, Management, RH',
      sectorValue: 'Commerce - Vente - Distribution',
    },
    {
      Name: 'Avocat',
      Description: 'Conseil et représentation juridique.',
      Domain: 'Droit, Sciences Politiques',
      Sector: 'Droit - Justice',
      Salary: '3 000 – 6 000 € / mois',
      BackgroundImage: img,
      Link: '/metiers/droit/avocat',
      domainValue: 'Droit, Sciences Politiques',
      sectorValue: 'Droit - Justice',
    },
    {
      Name: 'Chef de projet digital',
      Description: 'Pilotage de projets web et transformation numérique.',
      Domain: 'Marketing, Communication, Edition, Journalisme',
      Sector: 'Communication',
      Salary: '2 800 – 4 200 € / mois',
      BackgroundImage: img,
      Link: '/metiers/marketing/chef-projet-digital',
      domainValue: 'Marketing, Communication, Edition, Journalisme',
      sectorValue: 'Communication',
    },
    {
      Name: 'Agronome',
      Description: 'Expertise des sols, cultures et filières agricoles.',
      Domain: 'Agriculture, Alimentation, Environnement, Energie',
      Sector: 'Agriculture',
      Salary: '2 000 – 3 000 € / mois',
      BackgroundImage: img,
      Link: '/metiers/agriculture/agronome',
      domainValue: 'Agriculture, Alimentation, Environnement, Energie',
      sectorValue: 'Agriculture',
    },
    {
      Name: 'Éducateur spécialisé',
      Description: 'Accompagnement social de publics fragiles.',
      Domain: 'Santé, Paramédical, Social, Aide à la personne',
      Sector: 'Social',
      Salary: '1 800 – 2 400 € / mois',
      BackgroundImage: img,
      Link: '/metiers/social/educateur-specialise',
      domainValue: 'Santé, Paramédical, Social, Aide à la personne',
      sectorValue: 'Social',
    },
    {
      Name: 'Pilote de ligne',
      Description: 'Transport de passagers ou fret par avion.',
      Domain: 'Transport, Logistique, Distribution',
      Sector: 'Aéronautique et transport aérien',
      Salary: '4 500 – 8 000 € / mois',
      BackgroundImage: img,
      Link: '/metiers/transport/pilote-ligne',
      domainValue: 'Transport, Logistique, Distribution',
      sectorValue: 'Aéronautique et transport aérien',
    },
    {
      Name: 'Boulanger',
      Description: 'Fabrication de pains et viennoiseries.',
      Domain: 'Métiers de bouche',
      Sector: 'Métiers de bouche',
      Salary: '1 700 – 2 300 € / mois',
      BackgroundImage: img,
      Link: '/metiers/metiers-de-bouche/boulanger',
      domainValue: 'Métiers de bouche',
      sectorValue: 'Métiers de bouche',
    },
    {
      Name: 'Data scientist',
      Description: 'Modélisation statistique et exploitation de données.',
      Domain: 'Numérique, Digital, Informatique, Programmation',
      Sector: 'Cybersécurité',
      Salary: '3 500 – 5 500 € / mois',
      BackgroundImage: img,
      Link: '/metiers/numerique/data-scientist',
      domainValue: 'Numérique, Digital, Informatique, Programmation',
      sectorValue: 'Cybersécurité',
    },
  ]
  return rows.map(
    (row: Omit<MetiersSearchProfessionRow, 'id'>, index: number): MetiersSearchProfessionRow => ({
      ...row,
      id: index + 1,
    }),
  )
}
